import {
  Outlet,
  useLocation,
  useOutletContext,
  Link,
  useNavigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux"; // Added useDispatch
import { useState, useEffect, useCallback, useMemo } from "react"; // Added useMemo
import {
  setSegments,
  clearSegments,
} from "../../../../../store/redux_auth/authSlice"; // Added actions
import { getFilingList } from "../../../../../api/filings/filingAPI";
import { createExpertDemoFilings } from "../../../../../api/filings/demoAPI";
import Tooltip from "../../../../../components/ui/Tooltip";
import SegmentMenu from "../../../../../components/navegation/SegmentMenu";
import ModalNew from "../../../../../components/ui/popup/Modal";
import {
  PopupContent,
  PopupHeader,
} from "../../../../../components/ui/popup/common";
import SupportPopUp from "../../../../../components/ui/popup/SupportPopUp";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";

function TaxMainLayout() {
  const dispatch = useDispatch(); // Initialize dispatch
  const location = useLocation();
  const navigate = useNavigate();
  const rootContext = useOutletContext() || {};
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.user_role;

  // Data States
  const [filingList, setFilingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoAllowed, setDemoAllowed] = useState(false);
  const [servicesAvailable, setServicesAvailable] = useState(true);
  const [availableDemos, setAvailableDemos] = useState([
    { id: "PTF", name: "Personal Tax Filing Demo" },
  ]);

  // Modal States
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [demoStep, setDemoStep] = useState("selection");
  const [selectedDemos, setSelectedDemos] = useState({});
  const [showSupportPopup, setShowSupportPopup] = useState(false);

  const fetchFilings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFilingList("tax/filing");
      const data = await response.json();
      const filingsData = data.filings || data;
      setFilingList(Array.isArray(filingsData) ? filingsData : []);
      setDemoAllowed(data.demo_allowed || false);
      setServicesAvailable(data.services_available !== false);
      if (data.available_demos?.length > 0)
        setAvailableDemos(data.available_demos);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useRefreshOnAuth(fetchFilings);

  useEffect(() => {
    fetchFilings();
  }, [fetchFilings]);

  // --- START MODIFIED SECTION ---

  // 1. Memoize segments to ensure the reference only changes when data changes
  const segments = useMemo(() => {
    const hasCancelledFilings = filingList.some((f) => f.status === "CAN");
    const hasNewAvailable =
      !!rootContext.notificationState?.hasNewAvailableFilings;

    const items = [
      {
        path: "/tax-filing",
        text: "My Filings",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/filing_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/filing_blue.svg`,
      },
    ];

    if (userRole === "CPA") {
      items.push({
        path: "/tax-filing?segment=jobs",
        text: "Available Filings",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/filing_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/filing_blue.svg`,
        badge: hasNewAvailable ? "New" : null,
        onClick: rootContext.clearNewAvailableFilings,
      });
    }

    if (hasCancelledFilings) {
      items.push({
        path: "/tax-filing?segment=cancelled",
        text: "Cancelled Filings",
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/doc_filing_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/doc_filing_blue.svg`,
      });
    }

    return items;
    // Note: We use specific values for dependencies to avoid the "rootContext" loop
  }, [
    filingList,
    userRole,
    rootContext.notificationState?.hasNewAvailableFilings,
    rootContext.clearNewAvailableFilings,
  ]);

  // 2. The Teleportation Effect with an additional safety check (JSON.stringify)
  // to ensure we only dispatch when the actual CONTENT of the segments changes.
  const segmentsCheck = JSON.stringify(segments);
  useEffect(() => {
    if (segments.length > 0) {
      dispatch(setSegments(segments));
    }
    return () => dispatch(clearSegments());
  }, [dispatch, segmentsCheck]);

  // --- END MODIFIED SECTION ---

  const params = new URLSearchParams(location.search);
  const currentSegment = params.toString()
    ? `${location.pathname}?${params.toString().split("&")[0]}`
    : location.pathname;

  const handleStartDemo = async () => {
    const demoServices = Object.entries(selectedDemos)
      .filter(([_, d]) => d.selected)
      .map(([id, d]) => ({ service: id, invitation_code: d.invitationCode }));
    setDemoStep("loading");
    try {
      const res = await createExpertDemoFilings(demoServices);
      if (res.status === 201) setDemoStep("success");
      else setDemoStep("error");
    } catch {
      setDemoStep("error");
    }
  };

  const ctaElement = (
    <div className="segment-menu-cta-container">
      {userRole !== "CPA" &&
        (servicesAvailable ? (
          <Link
            to="/tax-filing/new-filing"
            className="cta-button primary rounded"
          >
            New Filing
          </Link>
        ) : (
          <div className="segment-menu-cta-disabled-group">
            <button className="cta-button primary disabled" disabled>
              New Filing
            </button>
            <Tooltip content="Our filing services are temporarily closed." />
          </div>
        ))}
      {demoAllowed && (
        <div className="segment-menu-cta-demo-group">
          {userRole === "CPA" ? (
            <button
              className="cta-button secondary rounded"
              onClick={() => setShowDemoPopup(true)}
            >
              Try Demo
            </button>
          ) : (
            <Link
              to="/tax-filing/new-filing?mode=DE"
              className="cta-button secondary rounded"
            >
              Try Demo
            </Link>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="main-content-area">
      <div className="content">
        <div className="ich-wrapper">
          <div className="internal-content-header">
            <SegmentMenu
              segments={segments}
              currentPath={currentSegment}
              cta={ctaElement}
              ctaGhostCount={1}
              loading={loading}
            />
          </div>
        </div>
        <div className="internal-content-body">
          <Outlet
            context={{ ...rootContext, filingList, loading, fetchFilings }}
          />
        </div>
      </div>

      {/* Demo Modal Logic ... */}
      {showDemoPopup && (
        <ModalNew className="content-modal" style={{ maxWidth: "520px" }}>
          <PopupHeader
            title="Welcome to the Plaintr demo experience"
            setOpenState={setShowDemoPopup}
            variant="compact"
          />
          <PopupContent>
            {demoStep === "selection" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                {availableDemos.map((demo) => (
                  <div
                    key={demo.id}
                    onClick={() =>
                      setSelectedDemos((p) => ({
                        ...p,
                        [demo.id]: { selected: !p[demo.id]?.selected },
                      }))
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedDemos[demo.id]?.selected || false}
                      readOnly
                    />
                    <span>{demo.name}</span>
                  </div>
                ))}
                <button
                  className="cta-button primary"
                  onClick={handleStartDemo}
                >
                  Start demo
                </button>
              </div>
            )}
          </PopupContent>
        </ModalNew>
      )}
      {showSupportPopup && (
        <SupportPopUp setIsModalOpen={setShowSupportPopup} />
      )}
    </div>
  );
}

export default TaxMainLayout;
